import { useEffect, useState } from "react";
import { ArrowClockwise, Envelope, CheckCircle, XCircle, DotsThreeCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  listNotificationLog,
  NOTIFICATION_KIND_LABELS,
  NOTIFICATION_STATUS_META,
} from "@/lib/notifications";

const LIMIT = 20;

/**
 * Admin-only read-only panel that lists the latest N rows from
 * notifications_log. Uses RLS (admin-select) rather than a backend
 * endpoint, so it automatically scopes to the admin's org.
 */
export default function NotificationLogPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const list = await listNotificationLog({ limit: LIMIT });
      setRows(list);
    } catch (err) {
      toast.error(err.message || "Could not load notifications");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onRefresh = () => { setRefreshing(true); load(); };

  const sent = rows.filter((r) => r.status === "sent").length;
  const failed = rows.filter((r) => r.status === "failed").length;

  return (
    <section
      data-testid="admin-notification-log"
      className="rounded-2xl border border-neutral-200 bg-white"
    >
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-100 px-5 py-4">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-secondary/30">
            <Envelope size={16} weight="duotone" className="text-primary-hover" />
          </span>
          <div>
            <p className="font-heading text-base font-semibold tracking-tight text-neutral-900">
              Notification log
            </p>
            <p className="text-xs text-neutral-500">
              {rows.length === 0
                ? "Transactional emails sent from the platform"
                : `Last ${rows.length} email${rows.length === 1 ? "" : "s"} · ${sent} sent · ${failed} failed`}
            </p>
          </div>
        </div>
        <button
          type="button"
          data-testid="admin-notification-log-refresh"
          onClick={onRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-600 hover:bg-neutral-50 disabled:opacity-60"
        >
          <ArrowClockwise size={11} weight="bold" className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <div className="h-6 w-6 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div data-testid="admin-notification-log-empty" className="p-8 text-center">
          <p className="text-sm text-neutral-500">
            No emails sent yet. Daily reports, incidents, invoices, and
            autopay failures will log here as they're dispatched.
          </p>
        </div>
      ) : (
        <ul data-testid="admin-notification-log-list" className="divide-y divide-neutral-100">
          {rows.map((r) => {
            const meta = NOTIFICATION_STATUS_META[r.status] || NOTIFICATION_STATUS_META.sent;
            const Icon = r.status === "sent" ? CheckCircle : r.status === "failed" ? XCircle : DotsThreeCircle;
            return (
              <li
                key={r.id}
                data-testid={`admin-notification-log-row-${r.id}`}
                className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <Icon
                    size={18}
                    weight="duotone"
                    className={
                      r.status === "sent"   ? "text-primary-hover" :
                      r.status === "failed" ? "text-[#C6851E]"   :
                                              "text-neutral-400"
                    }
                  />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate font-semibold text-neutral-900">{r.subject}</span>
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.tone}`}>
                        {meta.label}
                      </span>
                      <span className="inline-flex rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-[10px] font-semibold text-neutral-500">
                        {NOTIFICATION_KIND_LABELS[r.kind] || r.kind}
                      </span>
                    </div>
                    <p className="mt-0.5 truncate text-[11px] text-neutral-500">
                      {r.to_email} · {formatRelative(r.created_at)}
                      {r.error ? ` · ${r.error.slice(0, 80)}` : ""}
                    </p>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function formatRelative(iso) {
  if (!iso) return "—";
  try {
    const dt = new Date(iso);
    const mins = Math.round((Date.now() - dt.getTime()) / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return dt.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}
